import { _PdfDecodeStream } from './decode-stream';
import { _PdfDictionary } from './pdf-primitives';
import { FormatError } from './utils';
export class PdfPredictorStream extends _PdfDecodeStream {
    constructor(stream, maybeLength, params) {
        super(maybeLength);
        if (!(params instanceof _PdfDictionary)) {
            return stream;
        }
        const predictor = (this.predictor = params.get('Predictor') || 1);
        if (predictor <= 1) {
            return stream;
        }
        if (predictor !== 2 && (predictor < 10 || predictor > 15)) {
            throw new FormatError(`Unsupported predictor: ${predictor}`);
        }
        if (predictor === 2) {
            this.readBlock = this.readBlockTiff;
        }
        else {
            this.readBlock = this.readBlockPng;
        }
        this.stream = stream;
        this.dictionary = stream.dictionary;
        const colors = (this.colors = params.get('Colors') || 1);
        const bits = (this.bits = params.get('BPC', 'BitsPerComponent') || 8);
        const columns = (this.columns = params.get('Columns') || 1);
        this.pixBytes = (colors * bits + 7) >> 3;
        this.rowBytes = (columns * colors * bits + 7) >> 3;
        return this;
    }
    readBlockTiff() {
        const rowBytes = this.rowBytes;
        const bufferLength = this.bufferLength;
        const buffer = this.ensureBuffer(bufferLength + rowBytes);
        const bits = this.bits;
        const colors = this.colors;
        const rawBytes = this.stream.getBytes(rowBytes);
        this.eof = !rawBytes.length;
        if (this.eof) {
            return;
        }
        let inbuf = 0;
        let outbuf = 0;
        let inbits = 0;
        let outbits = 0;
        let position = bufferLength;
        let i;
        if (bits === 1 && colors === 1) {
            for (i = 0; i < rowBytes; ++i) {
                let c = rawBytes[i] ^ inbuf; // eslint-disable-line
                c ^= c >> 1;
                c ^= c >> 2;
                c ^= c >> 4;
                inbuf = (c & 1) << 7;
                buffer[position++] = c;
            }
        }
        else if (bits === 8) {
            for (i = 0; i < colors; ++i) {
                buffer[position++] = rawBytes[i]; // eslint-disable-line
            }
            for (; i < rowBytes; ++i) {
                buffer[position] = buffer[position - colors] + rawBytes[i]; // eslint-disable-line
                position++;
            }
        }
        else if (bits === 16) {
            const bytesPerPixel = colors * 2;
            for (i = 0; i < bytesPerPixel; ++i) {
                buffer[position++] = rawBytes[i]; // eslint-disable-line
            }
            for (; i < rowBytes; i += 2) {
                const sum = ((rawBytes[i] & 0xff) << 8) + // eslint-disable-line
                    (rawBytes[i + 1] & 0xff) +
                    ((buffer[position - bytesPerPixel] & 0xff) << 8) +
                    (buffer[position - bytesPerPixel + 1] & 0xff);
                buffer[position++] = (sum >> 8) & 0xff;
                buffer[position++] = sum & 0xff;
            }
        }
        else {
            const compArray = new Uint8Array(colors + 1);
            const bitMask = (1 << bits) - 1;
            let j = 0;
            let k = bufferLength;
            const columns = this.columns;
            for (i = 0; i < columns; ++i) {
                for (let kk = 0; kk < colors; ++kk) {
                    if (inbits < bits) {
                        inbuf = (inbuf << 8) | (rawBytes[j++] & 0xff);
                        inbits += 8;
                    }
                    compArray[kk] = (compArray[kk] + (inbuf >> (inbits - bits))) & bitMask; // eslint-disable-line
                    inbits -= bits;
                    outbuf = (outbuf << bits) | compArray[kk]; // eslint-disable-line
                    outbits += bits;
                    if (outbits >= 8) {
                        buffer[k++] = (outbuf >> (outbits - 8)) & 0xff;
                        outbits -= 8;
                    }
                }
            }
            if (outbits > 0) {
                buffer[k++] = (outbuf << (8 - outbits)) + (inbuf & ((1 << (8 - outbits)) - 1));
            }
        }
        this.bufferLength += rowBytes;
    }
    readBlockPng() {
        const rowBytes = this.rowBytes;
        const pixBytes = this.pixBytes;
        const predictor = this.stream.getByte();
        const rawBytes = this.stream.getBytes(rowBytes);
        this.eof = !rawBytes.length;
        if (this.eof) {
            return;
        }
        const bufferLength = this.bufferLength;
        const buffer = this.ensureBuffer(bufferLength + rowBytes);
        let prevRow = buffer.subarray(bufferLength - rowBytes, bufferLength);
        if (prevRow.length === 0) {
            prevRow = new Uint8Array(rowBytes);
        }
        let i;
        let j = bufferLength;
        let up;
        let c;
        switch (predictor) {
            case 0:
                for (i = 0; i < rowBytes; ++i) {
                    buffer[j++] = rawBytes[i]; // eslint-disable-line
                }
                break;
            case 1:
                for (i = 0; i < pixBytes; ++i) {
                    buffer[j++] = rawBytes[i]; // eslint-disable-line
                }
                for (; i < rowBytes; ++i) {
                    buffer[j] = (buffer[j - pixBytes] + rawBytes[i]) & 0xff; // eslint-disable-line
                    j++;
                }
                break;
            case 2:
                for (i = 0; i < rowBytes; ++i) {
                    buffer[j++] = (prevRow[i] + rawBytes[i]) & 0xff; // eslint-disable-line
                }
                break;
            case 3:
                for (i = 0; i < pixBytes; ++i) {
                    buffer[j++] = (prevRow[i] >> 1) + rawBytes[i]; // eslint-disable-line
                }
                for (; i < rowBytes; ++i) {
                    buffer[j] = (((prevRow[i] + buffer[j - pixBytes]) >> 1) + rawBytes[i]) & 0xff; // eslint-disable-line
                    j++;
                }
                break;
            case 4:
                for (i = 0; i < pixBytes; ++i) {
                    up = prevRow[i]; // eslint-disable-line
                    c = rawBytes[i]; // eslint-disable-line
                    buffer[j++] = up + c;
                }
                for (; i < rowBytes; ++i) {
                    up = prevRow[i]; // eslint-disable-line
                    const upLeft = prevRow[i - pixBytes];
                    const left = buffer[j - pixBytes];
                    const p = left + up - upLeft;
                    let pa = p - left;
                    if (pa < 0) {
                        pa = -pa;
                    }
                    let pb = p - up;
                    if (pb < 0) {
                        pb = -pb;
                    }
                    let pc = p - upLeft;
                    if (pc < 0) {
                        pc = -pc;
                    }
                    c = rawBytes[i]; // eslint-disable-line
                    if (pa <= pb && pa <= pc) {
                        buffer[j++] = left + c;
                    }
                    else if (pb <= pc) {
                        buffer[j++] = up + c;
                    }
                    else {
                        buffer[j++] = upLeft + c;
                    }
                }
                break;
            default:
                throw new FormatError(`Unsupported predictor: ${predictor}`);
        }
        this.bufferLength += rowBytes;
    }
}
